import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { AnalyticsContext, useTracking } from "../../lib/analyticsEvents";
import { defineStyles, useStyles } from "../hooks/useStyles";
import { postGetPageUrl, postGetLink, postGetLinkTarget, detectLinkpost } from "@/lib/collections/posts/helpers";
import { FeedPostMetaInfo, FeedItemSourceType } from "./ultraFeedTypes";
import { nofollowKarmaThreshold } from '@/lib/instanceSettings';
import { UltraFeedSettingsType, DEFAULT_SETTINGS } from "./ultraFeedSettingsTypes";
import { useUltraFeedObserver } from "./UltraFeedObserver";
import { useRecordPostView } from "../hooks/useRecordPostView";
import classnames from "classnames";
import { highlightMaxChars } from "../../lib/editor/ellipsize";
import { useOverflowNav } from "./OverflowNavObserverContext";
import { useDialog } from "../common/withDialog";
import UltraFeedPostDialog from "./UltraFeedPostDialog";
import FormatDate from "../common/FormatDate";
import PostActionsButton from "../dropdowns/posts/PostActionsButton";
import FeedContentBody from "./FeedContentBody";
import UltraFeedItemFooter from "./UltraFeedItemFooter";
import Loading from "../vulcan-core/Loading";
import OverflowNavButtons from "./OverflowNavButtons";
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import UltraFeedPostActions from "./UltraFeedPostActions";
import BookmarksIcon from "@/lib/vendor/@material-ui/icons/src/Bookmarks";
import ClockIcon from "@/lib/vendor/@material-ui/icons/src/AccessTime";
import SubscriptionsIcon from "@/lib/vendor/@material-ui/icons/src/NotificationsNone";
import LWTooltip from "../common/LWTooltip";
import { SparkleIcon } from "../icons/sparkleIcon";
import SeeLessFeedback from "./SeeLessFeedback";
import { useSeeLess } from "./useSeeLess";
import { UltraFeedCommentItem } from "./UltraFeedCommentItem";
import type { FeedCommentMetaInfo } from "./ultraFeedTypes";
import PostsUserAndCoauthors from "../posts/PostsUserAndCoauthors";
import TruncatedAuthorsList from "../posts/TruncatedAuthorsList";
import ForumIcon from "../common/ForumIcon";
import { RecombeeRecommendationsContextWrapper } from "../recommendations/RecombeeRecommendationsContextWrapper";
import { useUltraFeedContext } from "./UltraFeedContextProvider";

const localPostQuery = gql(`
  query LocalPostQuery($documentId: String!) {
    post(selector: { _id: $documentId }) {
      result {
        ...UltraFeedPostFragment
      }
    }
  }
`);

const styles = defineStyles("UltraFeedPostItem", (theme: ThemeType) => ({
  root: {
    position: 'relative',
    paddingTop: 12,
    paddingLeft: 20,
    paddingRight: 16,
    fontFamily: theme.palette.fonts.sansSerifStack,
    background: theme.palette.panelBackground.bannerAdTranslucentHeavy,
    backdropFilter: theme.palette.filters.bannerAdBlurHeavy,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    transition: 'background-color 1.0s ease-out',
    [theme.breakpoints.down('sm')]: {
      paddingTop: 16,
      paddingLeft: 20,
      paddingRight: 20,
    },
  },
  rootWithReadStyles: {
    backgroundColor: theme.palette.ultraFeed.readBackground,
    opacity: theme.palette.ultraFeed.readOpacity.root,
    '&:hover': {
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      backgroundColor: theme.palette.ultraFeed.readBackgroundMobile,
      borderTop: theme.palette.border.itemSeparatorBottom,
      borderBottom: theme.palette.border.itemSeparatorBottom,
      opacity: theme.palette.ultraFeed.readOpacity.rootMobile,
    },
  },
  rootWithAnimation: {
    backgroundColor: `${theme.palette.primary.main}3b`,
    transition: 'none',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  greyedOut: {
    opacity: 0.5,
    filter: 'blur(0.5px)',
    pointerEvents: 'none',
  },
  tripleDotMenu: {
    position: 'relative',
    bottom: 1,
    color: theme.palette.ultraFeed.dim,
    opacity: 0.7,
    "& svg": {
      fontSize: 18,
      cursor: "pointer",
    },
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      right: 16,
      top: 12,
      padding: 5,
      marginLeft: 4,
      zIndex: 10,
    },
  },
  header: {
    display: 'flex',
    marginBottom: 12,
    marginRight: -10, //so triple dot lines up on both posts and comments
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      position: 'relative',
      flexDirection: 'column',
      gap: '4px',
      alignItems: 'stretch',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'baseline',
    flexGrow: 1,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: '30px', // To leave space for the absolutely positioned triple-dot menu
    },
  },
  title: {
    fontFamily: theme.palette.fonts.sansSerifStack,
    fontWeight: 600,
    opacity: 0.8,
    lineHeight: 1.15,
    textWrap: 'balance',
    color: theme.palette.text.bannerAdOverlay,
    '&:hover': {
      opacity: 0.9,
      textDecoration: 'none',
      cursor: 'pointer',
    },
    fontSize: '1.3rem',
    whiteSpace: 'normal',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexGrow: 1,
      paddingRight: 8,
    },
  },
  titleIsRead: {
  },
  metaRow: {
    display: "flex",
    color: theme.palette.text.dim,
    fontFamily: theme.palette.fonts.sansSerifStack,
    fontSize: theme.typography.body2.fontSize,
    alignItems: 'baseline',
    flexShrink: 0,
    flexWrap: 'nowrap',
    columnGap: '8px',
    [theme.breakpoints.down('sm')]: {
      flexWrap: "nowrap",
      alignItems: "baseline",
      rowGap: "6px",
      flexShrink: 1,
      width: 'auto',
    },
  },
  sourceIcon: {
    width: 16,
    height: 16,
    color: theme.palette.grey[600],
    opacity: 0.7,
    position: 'relative',
    top: 2,
    flexShrink: 0,
  },
  metaDateContainer: {
    order: 3,
    [theme.breakpoints.down('sm')]: {
      order: 2,
      flexShrink: 0,
    },
  },
  footer: {
    marginTop: 12,
    marginBottom: 12,
  },
  footerGreyedOut: {
    opacity: 0.5,
    filter: 'blur(0.5px)',
    '& > *': {
      pointerEvents: 'none',
    },
    '& .SeeLessButton-root': {
      pointerEvents: 'auto !important',
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "20px 0",
  },
  authorsListWrapper: {
    flexGrow: 1,
    minWidth: 0,
    order: 2,
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileAuthorsListWrapper: {
    order: 1,
    minWidth: 0,
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  authorsList: {
    fontSize: 'inherit',
    color: 'inherit',
    fontFamily: 'inherit',
  },
  newCommentContainer: {
    marginTop: 16,
    marginLeft: -16,
    marginRight: -16,
    paddingLeft: 16,
    paddingRight: 16,
    borderTop: `1px solid ${theme.palette.greyAlpha(0.1)}`,
    paddingTop: 8,
  },
  sourceIconsContainer: {
    display: 'flex',
    alignItems: 'center',
    order: 1,
    gap: '4px',
    [theme.breakpoints.down('sm')]: {
      order: 3,
      flexShrink: 0,
    },
  },
  desktopTripleDotWrapper: {
    display: 'block',
    order: 4,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileTripleDotWrapper: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      position: 'absolute',
      right: -10,
      top: 0,
      height: 'auto',
      width: 'auto',
    },
  },
  contentWithReadStyles: {
    opacity: theme.palette.ultraFeed.readOpacity.content,
    '&:hover': {
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      opacity: theme.palette.ultraFeed.readOpacity.contentMobile,
    },
  },
}));

const sourceIconMap: Array<{ source: FeedItemSourceType, icon: any, tooltip: string }> = [
  { source: 'bookmarks' as FeedItemSourceType, icon: BookmarksIcon, tooltip: "From your bookmarks" },
  { source: 'subscriptionsPosts' as FeedItemSourceType, icon: SubscriptionsIcon, tooltip: "From users you follow" },
  { source: 'recombee-lesswrong-custom' as FeedItemSourceType, icon: SparkleIcon, tooltip: "Recommended for you" },
  { source: 'hacker-news' as FeedItemSourceType, icon: ClockIcon, tooltip: "Latest posts" },
];

interface UltraFeedPostItemHeaderProps {
  post: PostsListWithVotes;
  handleOpenDialog: () => void;
  sources: FeedItemSourceType[];
  isSeeLessMode: boolean;
  handleSeeLess: () => void;
  postMetaInfo: FeedPostMetaInfo;
}

const UltraFeedPostItemHeader = ({
  post,
  handleOpenDialog,
  sources,
  handleSeeLess,
  isSeeLessMode,
  postMetaInfo,
}: UltraFeedPostItemHeaderProps) => {
  const classes = useStyles(styles);
  const metaRowRef = useRef<HTMLDivElement>(null);
  const { feedType } = useUltraFeedContext();
  
  const isSubscribedFeed = feedType === 'following';
  const isFromSubscribedSource = postMetaInfo.sources.includes('subscriptionsPosts' as const);

  const handleTitleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      handleOpenDialog();
    }
  };

  const sourceIcons = sourceIconMap
    .filter(({ source }) => sources.includes(source))
    .map(({ source, icon, tooltip }) => ({ icon, tooltip, key: source }));

  const { isLinkpost, linkpostDomain } = detectLinkpost(post);

  return (
    <div className={classes.header}>
      <div className={classes.titleContainer}>
        <a
          href={postGetPageUrl(post)}
          onClick={handleTitleClick}
          className={classes.title}
        >
          {post.title}
        </a>
      </div>
      <div className={classes.metaRow} ref={metaRowRef}>
        <div className={classes.mobileAuthorsListWrapper}>
          <TruncatedAuthorsList 
            post={post} 
            useMoreSuffix={false} 
            expandContainer={metaRowRef} 
            className={classes.authorsList} 
            useUltraFeedModal 
            showSubscribedIcon={isSubscribedFeed && isFromSubscribedSource}
          />
        </div>
        <div className={classes.authorsListWrapper}>
          <PostsUserAndCoauthors 
            post={post} 
            abbreviateIfLong={true} 
            tooltipPlacement="top" 
            compact 
            useUltraFeedModal 
            showSubscribedIcon={isSubscribedFeed && isFromSubscribedSource}
          />
        </div>
        {post.postedAt && (
          <span className={classes.metaDateContainer}>
            <FormatDate date={post.postedAt} />
          </span>
        )}
        <div className={classes.sourceIconsContainer}>
          {sourceIcons.map((iconInfo) => (
            <LWTooltip key={iconInfo.key} title={iconInfo.tooltip} placement="top">
              <span>
                <iconInfo.icon className={classes.sourceIcon} />
              </span>
            </LWTooltip>
          ))}
          {isLinkpost && (
            <LWTooltip title={`Linkpost from ${linkpostDomain}`} placement="top">
              <a href={postGetLink(post)} target={postGetLinkTarget(post)} onClick={(e) => e.stopPropagation()}>
                <ForumIcon icon="Link" className={classes.sourceIcon} />
              </a>
            </LWTooltip>
          )}
        </div>
        <div className={classes.desktopTripleDotWrapper}>
          <AnalyticsContext pageElementContext="tripleDotMenu">
            <PostActionsButton
              post={post}
              vertical={true}
              autoPlace
              includeBookmark
              onSeeLess={handleSeeLess}
              isSeeLessMode={isSeeLessMode}
              ActionsComponent={UltraFeedPostActions}
              className={classnames(classes.tripleDotMenu, { [classes.greyedOut]: isSeeLessMode })}
            />
          </AnalyticsContext>
        </div>
      </div>
    </div>
  );
};

const UltraFeedPostItem = ({
  post,
  postMetaInfo,
  index,
  settings = DEFAULT_SETTINGS,
  isHighlightAnimating = false,
}: {
  post: PostsListWithVotes,
  postMetaInfo: FeedPostMetaInfo,
  index: number,
  showKarma?: boolean,
  settings?: UltraFeedSettingsType,
  isHighlightAnimating?: boolean,
}) => {
  const classes = useStyles(styles);
  const { observe, trackExpansion } = useUltraFeedObserver();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const { openDialog } = useDialog();
  const { openInNewTab, feedType } = useUltraFeedContext();
  const overflowNav = useOverflowNav(elementRef);
  const { captureEvent } = useTracking();
  const { recordPostView } = useRecordPostView(post);
  const [hasRecordedViewOnExpand, setHasRecordedViewOnExpand] = useState(false);
  const { displaySettings } = settings;
  const needsFullPostInitially = displaySettings.postInitialWords > (highlightMaxChars / 5);
  const [isLoadingFull, setIsLoadingFull] = useState(needsFullPostInitially);
  const [resetSig, setResetSig] = useState(0);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [newComment, setNewComment] = useState<UltraFeedComment | null>(null);
  const [newCommentMetaInfo, setNewCommentMetaInfo] = useState<FeedCommentMetaInfo | null>(null);

  const {
    isSeeLessMode,
    handleSeeLessClick,
    handleFeedbackChange,
  } = useSeeLess({
    documentId: post._id,
    collectionName: 'Posts',
    metaInfo: postMetaInfo,
  });

  const { data: fullPostData, loading: loadingFullPost } = useQuery(localPostQuery, {
    skip: !isLoadingFull,
    fetchPolicy: "cache-first",
    variables: {
      documentId: post._id,
    },
  });

  const fullPost = fullPostData?.post?.result;

  useEffect(() => {
    const currentElement = elementRef.current;
    if (currentElement) {
      observe(currentElement, { 
        documentId: post._id, 
        documentType: 'post',
        servedEventId: postMetaInfo.servedEventId,
        feedCardIndex: index
      });
    }
  }, [observe, post._id, postMetaInfo.servedEventId, index]);

  const handleContentExpand = useCallback((expanded: boolean, wordCount: number) => {
    setIsContentExpanded(expanded);
    
    if (expanded && !isLoadingFull && !fullPost) {
      setIsLoadingFull(true);
    }

    trackExpansion({
      documentId: post._id,
      documentType: 'post',
      level: expanded ? 1 : 0,
      maxLevelReached: expanded,
      wordCount,
      servedEventId: postMetaInfo.servedEventId,
      feedCardIndex: index,
    });

    captureEvent("ultraFeedPostItemExpanded", {
      expanded,
      wordCount,
    });

    if (!hasRecordedViewOnExpand) {
      void recordPostView({ post, extraEventProperties: { type: 'ultraFeedExpansion' } });
      setHasRecordedViewOnExpand(true);
    }

  }, [
    trackExpansion, 
    post, 
    captureEvent, 
    recordPostView, 
    hasRecordedViewOnExpand, 
    isLoadingFull, 
    fullPost,
    postMetaInfo.servedEventId,
    index,
  ]);

  const handleCollapse = () => {
    setResetSig((s) => s + 1);
    setIsContentExpanded(false);
  };

  const handleReplyClick = useCallback(() => {
    setIsReplying(!isReplying);
  }, [isReplying]);

  const handleReplySubmit = useCallback((newComment: UltraFeedComment) => {
    setIsReplying(false);
    setNewComment(newComment);
    
    const defaultMetaInfo: FeedCommentMetaInfo = {
      displayStatus: 'expanded',
      sources: [],
      descendentCount: 0,
      directDescendentCount: 0,
      highlight: false,
      lastServed: new Date(),
      lastViewed: null,
      lastInteracted: new Date(),
      postedAt: newComment.postedAt ? new Date(newComment.postedAt) : new Date(),
    };
    setNewCommentMetaInfo(defaultMetaInfo);
  }, []);

  const handleReplyCancel = useCallback(() => {
    setIsReplying(false);
  }, []);

  const handleCommentEdit = useCallback((editedComment: CommentsList) => {
    if (newComment && editedComment._id === newComment._id) {
      setNewComment({
        ...editedComment,
        post,
      });
    }
  }, [newComment, post]);

  const replyConfig = useMemo(() => ({
    isReplying,
    onReplyClick: handleReplyClick,
    onReplySubmit: handleReplySubmit,
    onReplyCancel: handleReplyCancel,
  }), [isReplying, handleReplyClick, handleReplySubmit, handleReplyCancel]);

  const handleOpenDialog = useCallback((location: "title" | "content") => {
    captureEvent("ultraFeedPostDialogOpened", { location });
    trackExpansion({
      documentId: post._id,
      documentType: 'post',
      level: 1,
      maxLevelReached: true,
      wordCount: post.contents?.wordCount ?? 0,
      servedEventId: postMetaInfo.servedEventId,
      feedCardIndex: index,
    });
    
    if (!hasRecordedViewOnExpand) {
      void recordPostView({ post, extraEventProperties: { type: 'ultraFeedExpansion' } });
      setHasRecordedViewOnExpand(true);
    }
    
    if (openInNewTab) {
      const postUrl = `/posts/${post._id}/${post.slug}`;
      window.open(postUrl, '_blank');
    } else {
      openDialog({
        name: "UltraFeedPostDialog",
        closeOnNavigate: true,
        contents: ({ onClose }) => (
          <UltraFeedPostDialog
            {...(fullPost ? { post: fullPost } : { partialPost: post })}
            postMetaInfo={postMetaInfo}
            onClose={onClose}
          />
        )
      });
    }
  }, [
    openDialog,
    post,
    captureEvent,
    fullPost,
    trackExpansion,
    postMetaInfo,
    openInNewTab,
    hasRecordedViewOnExpand,
    recordPostView,
    index,
  ]);

  const { isRead } = postMetaInfo;

  const shortformHtml = post.shortform 
    ? `This is a special post for quick takes (aka "shortform"). Only the owner can create top-level comments.`
    : undefined

  const displayHtml = fullPost?.contents?.html ?? post.contents?.htmlHighlight ?? shortformHtml;
  
  const displayWordCount = post.shortform ? 0 : post.contents?.wordCount;

  const truncationParams = useMemo(() => {
    return {
      initialWordCount: displaySettings.postInitialWords,
      maxWordCount: displaySettings.postMaxWords
    };
  }, [displaySettings.postInitialWords, displaySettings.postMaxWords]);

  if (!displayHtml) {
    return null; 
  }
  

  return (
    <RecombeeRecommendationsContextWrapper postId={post._id} recommId={postMetaInfo.recommInfo?.recommId}>
    <AnalyticsContext ultraFeedElementType="feedPost" postId={post._id} feedCardIndex={index} ultraFeedSources={postMetaInfo.sources}>
    <div className={classnames(classes.root, { 
      [classes.rootWithAnimation]: isHighlightAnimating,
      [classes.rootWithReadStyles]: feedType === 'following' && isRead,
    })}>
      <div ref={elementRef} className={classnames(classes.mainContent, { 
        [classes.contentWithReadStyles]: feedType === 'following' && isRead,
      })}>
        {/* On small screens, the triple dot menu is positioned absolutely to the root */}
        <div className={classes.mobileTripleDotWrapper}>
          <AnalyticsContext pageElementContext="tripleDotMenu">
            <PostActionsButton
              post={post}
              vertical={true}
              autoPlace
              onSeeLess={handleSeeLessClick}
              isSeeLessMode={isSeeLessMode}
              ActionsComponent={UltraFeedPostActions}
              includeBookmark={true}
              className={classnames(classes.tripleDotMenu, { [classes.greyedOut]: isSeeLessMode })}
            />
          </AnalyticsContext>
        </div>

        <div className={classnames({ [classes.greyedOut]: isSeeLessMode })}>
          <UltraFeedPostItemHeader
            post={post}
            handleOpenDialog={() => handleOpenDialog("title")}
            sources={postMetaInfo.sources}
            isSeeLessMode={isSeeLessMode}
            handleSeeLess={handleSeeLessClick}
            postMetaInfo={postMetaInfo}
          />
        </div>

        {isSeeLessMode && (
          <SeeLessFeedback
            onUndo={handleSeeLessClick}
            onFeedbackChange={handleFeedbackChange}
          />
        )}
        
        {!isSeeLessMode && (
          <FeedContentBody
            html={displayHtml}
            initialWordCount={truncationParams.initialWordCount}
            maxWordCount={truncationParams.maxWordCount}
            wordCount={displayWordCount ?? 200}
            nofollow={(post.user?.karma ?? 0) < nofollowKarmaThreshold.get()}
            onContinueReadingClick={() => handleOpenDialog("content")}
            onExpand={handleContentExpand}
            hideSuffix={loadingFullPost}
            resetSignal={resetSig}
          />
        )}
        
        {/* Show loading indicator below content if we're loading the full post */}
        {loadingFullPost && displayHtml && !isSeeLessMode && (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        )}
        
        <UltraFeedItemFooter 
          document={post} 
          collectionName="Posts" 
          metaInfo={postMetaInfo} 
          className={classnames(classes.footer, { [classes.footerGreyedOut]: isSeeLessMode })}
          replyConfig={replyConfig}
        />
        
        {/* Show new comment if one was just created */}
        {newComment && newCommentMetaInfo && !isSeeLessMode && (
          <div className={classes.newCommentContainer}>
            <UltraFeedCommentItem
              comment={newComment}
              metaInfo={newCommentMetaInfo}
              onChangeDisplayStatus={() => {}}
              showPostTitle={false}
              highlight={false}
              isFirstComment={true}
              isLastComment={true}
              settings={settings}
              parentAuthorName={null}
              isHighlightAnimating={false}
              replyConfig={{
                isReplying: false,
                onReplyClick: () => {},
                onReplySubmit: () => {},
                onReplyCancel: () => {},
              }}
              cannotReplyReason="You cannot reply to your own comment within the feed"
              onEditSuccess={handleCommentEdit}
              threadIndex={index}
              commentIndex={0}
            />
          </div>
        )}
      </div>
      
      {(overflowNav.showUp || overflowNav.showDown) && <OverflowNavButtons nav={overflowNav} onCollapse={isContentExpanded ? handleCollapse : undefined} />}
    </div>
    </AnalyticsContext>
    </RecombeeRecommendationsContextWrapper>
  );
};

export default UltraFeedPostItem;
